/**
 * The QR payload the legacy prints and the field scanner reads (ADR-029 §1) — a wire contract with already-printed
 * labels, reproduced BYTE FOR BYTE (`ARLMS_QRCode.js:236-260` / `ARLMS_FacilitySelection.js:377-405`).
 *
 * Two forms:
 *  1. `facilityIds[<parentId>-<id>,…]` — the scanner regexes `[0-9]+-[0-9]+` and keeps the id after the dash. A ROOT
 *     prints `0-<id>`: the legacy tree picker initialises `parentId = 0` (`ARLMS_FacilityTree.js:454`), and `0-` is
 *     what its regex matches (a `null-` would not).
 *  2. `<deepLinkBase>?arc_facility=<id>` — a plain deep link into this app, printed when the realm's `qrCodeWithLink`
 *     is on (the legacy global default) AND exactly one facility was chosen. Both print kinds (label and AR anchor)
 *     render the SAME string — the legacy builds one `qrCodeString` for both.
 */
export interface QrFacility {
  id: number;
  parentId: number | null;
  name: string;
}

/** The legacy's cap ("bis zu 10 Bereiche"), with the recommendation of one. */
export const QR_MAX_FACILITIES = 10;

export function qrPayload(facilities: readonly QrFacility[], opts: { withLink: boolean; deepLinkBase: string }): string {
  if (facilities.length === 0) return "";
  if (opts.withLink && facilities.length === 1) return `${opts.deepLinkBase}?arc_facility=${String(facilities[0]!.id)}`;
  return `facilityIds[${facilities.map((f) => `${String(f.parentId ?? 0)}-${String(f.id)}`).join(",")}]`;
}

/**
 * The label's second line, as the legacy words it: the first facility, then "+N more".
 *
 * Returns the REMAINDER as a number rather than a phrase. This module is pure payload logic with no translator of
 * its own, and the phrase it used to build was a hand-written English plural — the exact shape ADR-048 removes
 * everywhere else. The caller renders it through i18next's `_one`/`_other` (diff panel).
 */
export function qrLabelLines(facilities: readonly QrFacility[]): { name: string; rest: number } {
  const first = facilities[0];
  if (!first) return { name: "", rest: 0 };
  return { name: first.name, rest: facilities.length - 1 };
}
